/**
 * Progress of the seven process steps, derived from list data the app loads anyway
 * (measures, funding programs, schemes). Pure, so it can be tested without a server.
 */
import type { FundingProgram, MeasureSummary, Scheme, SchemeKind } from '../api/types'

export type StepStatus = 'done' | 'open' | 'unknown'

export interface StepProgress {
  status: StepStatus
  /** Short state for screen readers and the step list, e.g. "erledigt" or "offen". */
  text: string
  /** Optional detail for the tooltip, e.g. "2 von 5 Maßnahmen bewertet". */
  detail?: string
}

export interface ProgressInput {
  measures?: readonly MeasureSummary[]
  programs?: readonly FundingProgram[]
  schemes?: readonly Scheme[]
}

/** Progress per step number (1–7). Steps whose data is not loaded yet are "unknown". */
export type ProcessProgress = Record<1 | 2 | 3 | 4 | 5 | 6 | 7, StepProgress>

const UNKNOWN: StepProgress = { status: 'unknown', text: '' }
const done = (text = 'erledigt', detail?: string): StepProgress => ({ status: 'done', text, detail })
const open = (detail?: string, text = 'offen'): StepProgress => ({ status: 'open', text, detail })

const count = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`

/** Schemes come with default weights, so they are always set up; custom weights count as "angepasst". */
function schemeProgress(schemes: readonly Scheme[] | undefined, kind: SchemeKind): StepProgress {
  if (!schemes) return UNKNOWN
  const scheme = schemes.find((s) => s.kind === kind)
  const customized = Boolean(scheme?.criteria.some((c) => c.customWeight !== null && c.customWeight !== c.defaultWeight))
  return customized ? done('angepasst', 'Eigene Gewichte') : done('eingerichtet', 'Standardgewichte')
}

export function deriveProgress({ measures, programs, schemes }: ProgressInput): ProcessProgress {
  const total = measures?.length ?? 0
  const prioritized = measures?.filter((m) => m.prioritization.complete).length ?? 0
  const assessments = measures?.reduce((sum, m) => sum + m.assessedPrograms, 0) ?? 0
  const provisional = measures?.some((m) => m.provisional) ?? false

  return {
    1: !measures ? UNKNOWN : total ? done('erledigt', count(total, 'Maßnahme erfasst', 'Maßnahmen erfasst')) : open('Noch keine Maßnahme erfasst'),
    2: schemeProgress(schemes, 'PRIORITIZATION'),
    3: !measures
      ? UNKNOWN
      : !total
        ? open('Noch keine Maßnahme erfasst')
        : prioritized === total
          ? done('erledigt', 'Alle Maßnahmen bewertet')
          : open(`${prioritized} von ${count(total, 'Maßnahme', 'Maßnahmen')} vollständig bewertet`),
    4: !programs
      ? UNKNOWN
      : programs.length
        ? done('erledigt', count(programs.length, 'Förderprogramm erfasst', 'Förderprogramme erfasst'))
        : open('Noch kein Förderprogramm erfasst'),
    5: schemeProgress(schemes, 'FUNDING'),
    6: !measures ? UNKNOWN : assessments ? done('erledigt', count(assessments, 'Bewertung', 'Bewertungen')) : open('Noch keine Förderbewertung'),
    7: !measures
      ? UNKNOWN
      : !total
        ? open('Noch keine Ergebnisse')
        : provisional
          ? open('Ergebnis beruht auf unvollständigen Bewertungen', 'vorläufig')
          : done('verfügbar', 'Ergebnis vollständig'),
  }
}
